import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CloseIcon, HelpIcon, OpenInNewIcon } from '@platform/ui-kit';

/**
 * "How this works" — the per-screen help panel, rebuilt from APR.
 *
 * Same content model as APR's: what the screen is FOR, what you DO here, where it
 * FITS, what to do FIRST, then definition lists for the terms that are easy to get
 * wrong. The wording is APR's, verbatim, and is expected to change as the
 * applications are repositioned — which is why every screen's copy is a plain data
 * object at the top of its file rather than prose buried in the markup.
 *
 * Rendered as a right-hand Drawer, matching the record drawers, so the platform has
 * one way of showing a side panel rather than two.
 */

export interface HelpDefinition {
  readonly term: string;
  /** Swatch colour, carried over from APR so the terms stay recognisable. */
  readonly color?: string;
  readonly definition: string;
}

export interface HelpSection {
  readonly label: string;
  readonly definitions?: readonly HelpDefinition[];
  readonly paragraphs?: readonly string[];
}

export interface HelpContent {
  readonly title: string;
  /** The glyph APR heads this screen's help with — 📱 for Applications, 💰 for Cost. */
  readonly icon: string;
  readonly purpose: string;
  readonly doHere: readonly string[];
  readonly fits: string;
  readonly firstTime: string;
  readonly sections?: readonly HelpSection[];
  readonly links?: readonly { readonly label: string; readonly path: string }[];
}

const WIDTH = 520;

function Label({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.8, mb: 1 }}
    >
      {children}
    </Typography>
  );
}

export function HelpPanel({
  help,
  onNavigate,
}: {
  help: HelpContent;
  onNavigate?: (path: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* APR's launcher pill: #1565c0 on #eef4ff, 1.25px #c5d9f5, radius 20px. An
          outlined transparent button is the platform default and reads as a form
          control rather than a help affordance. */}
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: '14px',
          py: '6px',
          fontSize: 13,
          fontWeight: 600,
          font: 'inherit',
          fontFamily: 'inherit',
          cursor: 'pointer',
          borderRadius: '20px',
          border: '1.25px solid #c5d9f5',
          color: '#1565c0',
          bgcolor: '#eef4ff',
          '&:hover': { bgcolor: '#e2ecfd' },
        }}
      >
        <HelpIcon sx={{ fontSize: 16 }} />
        How this works
      </Box>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        /*
          ABOVE THE APP BAR — why the heading looked missing.

          The owner (2026-08-08): "the How it works section for master data — the head is
          missing." It was never missing. A Drawer is a modal, and the shell raises its top
          bar over drawers, so the bar painted over the panel's top 64px — precisely the
          strip holding the icon, the title and the close button. The body below it looked
          fine, which is what made it read as a missing header rather than a covered one.

          `master-data/ApplicationDetailDrawer` carries the same line for the same reason,
          and the help panels in APR, SPN and Inventory Registry avoid it only because they
          are hand-rolled with their own z-index rather than MUI Drawers. This is the one
          panel built as a Drawer, and the one that lost its head.
        */
        sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
        PaperProps={{ sx: { width: { xs: '100%', sm: WIDTH }, maxWidth: '100%' } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Box component="span" sx={{ fontSize: 20, lineHeight: 1 }}>
            {help.icon}
          </Box>
          <Typography sx={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: 700 }}>
            {help.title}
          </Typography>
          <IconButton onClick={() => setOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box sx={{ px: 3, py: 2.5, overflowY: 'auto' }}>
          <Stack spacing={3}>
            <Typography variant="body2">{help.purpose}</Typography>

            <Box>
              <Label>What you do here</Label>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {help.doHere.map((item) => (
                  <Typography component="li" variant="body2" key={item} sx={{ mb: 0.75 }}>
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>

            <Box>
              <Label>Where it fits</Label>
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  fontFamily: 'monospace',
                  fontSize: 13,
                }}
              >
                {help.fits}
              </Box>
            </Box>

            {/* The one instruction that gets someone unstuck, called out on its own. */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 1,
                borderLeft: 4,
                borderColor: 'success.main',
                bgcolor: 'action.hover',
              }}
            >
              <Label>First time? Do this</Label>
              <Typography variant="body2">{help.firstTime}</Typography>
            </Box>

            {help.sections?.map((section) => (
              <Box key={section.label}>
                <Divider sx={{ mb: 2 }} />
                <Label>{section.label}</Label>

                {section.definitions?.map((entry) => (
                  <Box key={entry.term} sx={{ mb: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                      {entry.color ? (
                        <Box
                          aria-hidden
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: entry.color,
                            flexShrink: 0,
                          }}
                        />
                      ) : null}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {entry.term}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {entry.definition}
                    </Typography>
                  </Box>
                ))}

                {section.paragraphs?.map((paragraph) => (
                  <Typography variant="body2" color="text.secondary" key={paragraph} sx={{ mb: 1.5 }}>
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            ))}

            {help.links?.length ? (
              <Box>
                <Divider sx={{ mb: 2 }} />
                <Label>Related screens</Label>
                <Stack spacing={0.5} alignItems="flex-start">
                  {help.links.map((link) => (
                    <Button
                      key={link.path}
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => {
                        setOpen(false);
                        onNavigate?.(link.path);
                      }}
                      disabled={!onNavigate}
                    >
                      {link.label}
                    </Button>
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}
